import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./TryNow.css";
import {
  ensureReferenceDataCached,
  fetchReferenceDataReservations,
  fetchResultPreview,
  getReferenceDataFromStorage,
  saveTryNowJourney,
  type ReferenceDataReservation,
  type ResultPreviewResponse,
} from "../../hallticket-lib";

const MARKETING_HOME = "https://www.hallticket.in/";
const MARKETING_HOW = "https://www.hallticket.in/#how";
const MARKETING_PRICING = "https://www.hallticket.in/#pricing";
const TRY_NOW_POST_AUTH_REDIRECT = "/dashboard/results";

const PERCENTILE_MIN = 0;
const PERCENTILE_MAX = 100;

function getPercentileValidationError(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return "Enter your percentile.";
  }
  const n = Number.parseFloat(trimmed);
  if (!Number.isFinite(n)) {
    return "Enter a valid number.";
  }
  if (n < PERCENTILE_MIN || n > PERCENTILE_MAX) {
    return `Percentile must be between ${PERCENTILE_MIN} and ${PERCENTILE_MAX}.`;
  }
  return null;
}

const RESULTS_PREVIEW_SKELETON_ROWS = 8;

type TerminalStep = { label: string; sub: string; meta: string; dur: number };

const BASE_TERMINAL_STEPS: TerminalStep[] = [
  { label: 'Validating your inputs', sub: 'percentile · category · quota', meta: 'OK', dur: 700 },
  { label: 'Loading 5-year CAP-round dataset', sub: '2021 — 2025 closing percentiles', meta: '5 yrs', dur: 900 },
  { label: 'Matching against 312 colleges', sub: 'Maharashtra autonomous + government + aided', meta: '312 / 312', dur: 1100 },
  { label: 'Filtering by category & quota', sub: 'applying your eligibility envelope', meta: '—', dur: 850 },
  { label: 'Ranking by safety / match / reach', sub: 'three-band probability model', meta: 'sorted', dur: 950 },
  { label: 'Cross-checking ladies / minority seats', sub: 'where applicable to your inputs', meta: 'done', dur: 700 },
  { label: 'Building your shortlist', sub: 'finalising your shortlist', meta: '…', dur: 800 },
];

export function TryNow() {
  const percentileInputRef = useRef<HTMLInputElement>(null);
  const cachedReferenceData = getReferenceDataFromStorage();

  useEffect(() => {
    void (async () => {
      try {
        const [referenceData, reservations] = await Promise.all([
          ensureReferenceDataCached(),
          fetchReferenceDataReservations(),
        ]);

        if (referenceData?.exams.length) {
          setExamOptions(referenceData.exams);
          setExam((prev) => (prev && referenceData.exams.includes(prev) ? prev : referenceData.exams[0]));
        }
        if (referenceData?.genders.length) {
          setGenderOptions(referenceData.genders);
          setGender((prev) => {
            if (!prev) {
              return referenceData.genders[0];
            }
            const match = referenceData.genders.find((option) => option.toLowerCase() === prev.toLowerCase());
            return match ?? referenceData.genders[0];
          });
        }

        const vertical = reservations.filter((item) => item.type === "Vertical");
        const horizontal = reservations.filter((item) => item.type === "Horizontal");
        setVerticalReservations(vertical);
        setHorizontalReservations(horizontal);
        setCategory((prev) => prev || vertical[0]?.name || "");
      } catch {
        const fallbackReferenceData = getReferenceDataFromStorage();
        setExamOptions(fallbackReferenceData?.exams ?? []);
        setGenderOptions(fallbackReferenceData?.genders ?? []);
        setVerticalReservations([]);
        setHorizontalReservations([]);
      }
    })();
  }, []);

  // State
  const [stage, setStage] = useState<'form' | 'terminal' | 'results'>('form');
  const [examOptions, setExamOptions] = useState<string[]>(() => cachedReferenceData?.exams ?? []);
  const [genderOptions, setGenderOptions] = useState<string[]>(() => cachedReferenceData?.genders ?? []);
  const [exam, setExam] = useState(() => cachedReferenceData?.exams[0] ?? "");
  const [percentile, setPercentile] = useState('');
  const [percentileError, setPercentileError] = useState<string | null>(null);
  const [verticalReservations, setVerticalReservations] = useState<ReferenceDataReservation[]>([]);
  const [horizontalReservations, setHorizontalReservations] = useState<ReferenceDataReservation[]>([]);
  const [category, setCategory] = useState("");
  const [gender, setGender] = useState(() => cachedReferenceData?.genders[0] ?? "");
  const [quotas, setQuotas] = useState<string[]>([]);
  
  // Terminal State
  const [activeStep, setActiveStep] = useState(-1);
  const [visibleSteps, setVisibleSteps] = useState<number[]>([]);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [previewStats, setPreviewStats] = useState<ResultPreviewResponse | null>(null);

  const terminalSteps = useMemo(() => {
    const last = BASE_TERMINAL_STEPS.length - 1;
    const n = previewStats?.collegesMatched;
    return BASE_TERMINAL_STEPS.map((step, i) => {
      if (i !== last) {
        return step;
      }
      return {
        ...step,
        sub: n != null ? `finalising ${n} colleges` : step.sub,
        meta: n != null ? String(n) : step.meta,
      };
    });
  }, [previewStats]);

  // Handlers
  const handlePercentileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let v = e.target.value.replace(/[^0-9.]/g, '');
    const parts = v.split('.');
    if (parts.length > 2) v = parts[0] + '.' + parts.slice(1).join('');
    setPercentile(v);
    setPercentileError(null);
  };

  const handlePercentileBlur = () => {
    if (percentile.trim() === "") {
      setPercentileError(null);
      return;
    }
    setPercentileError(getPercentileValidationError(percentile));
  };

  const handleRunResultReview = async () => {
    const err = getPercentileValidationError(percentile);
    setPercentileError(err);
    if (err) {
      percentileInputRef.current?.focus();
      return;
    }
    if (!category.trim()) {
      return;
    }
    
    setPreviewStats(null);
    setStage('terminal');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const percentileNum = Number.parseFloat(percentile.trim());
    const previewPromise = fetchResultPreview({
      percentile: percentileNum,
      gender: gender.trim(),
      category: category.trim(),
      exam: exam.trim(),
    })
      .then((data) => {
        setPreviewStats(data);
        return data;
      })
      .catch(() => {
        setPreviewStats(null);
        return null;
      });

    for (let i = 0; i < BASE_TERMINAL_STEPS.length; i++) {
      setTimeout(() => {
        setVisibleSteps(prev => [...prev, i]);
      }, 80 + i * 60);
    }

    await new Promise(r => setTimeout(r, 400));

    for (let i = 0; i < BASE_TERMINAL_STEPS.length; i++) {
      setActiveStep(i);
      await new Promise(r => setTimeout(r, BASE_TERMINAL_STEPS[i].dur));
      setCompletedSteps(prev => [...prev, i]);
    }

    setActiveStep(-1);
    await previewPromise;
    await new Promise(r => setTimeout(r, 400));
    setStage('results');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAuthHandoff = () => {
    saveTryNowJourney({
      exam,
      percentile,
      category,
      gender,
      quotas,
    });
  };

  const toggleQuota = (name: string) => {
    setQuotas((prev) =>
      prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name],
    );
  };

  const renderChips = () => {
    return (
      <div className="chips">
        <span className="chip"><strong>{exam || '—'}</strong></span>
        <span className="chip"><strong>{percentile || '—'}</strong> percentile</span>
        <span className="chip"><strong>{category || '—'}</strong></span>
        <span className="chip">{gender || '—'}</span>
        {quotas.map((item) => (
          <span key={item} className="chip">{item}</span>
        ))}
      </div>
    );
  };

  const percentileValidForSubmit =
    getPercentileValidationError(percentile) === null
    && category.trim() !== ""
    && exam.trim() !== ""
    && gender.trim() !== "";

  return (
    <div className="try-now-page">
      <header className="nav">
        <div className="wrap">
          <a href={MARKETING_HOME} className="logoE" rel="noopener noreferrer">
            HallTicket
          </a>
          <div className="nav-right">
            <a href={MARKETING_HOW} rel="noopener noreferrer">
              How it works
            </a>
            <a href={MARKETING_PRICING} rel="noopener noreferrer">
              Pricing
            </a>
            <Link to="/signin" style={{ color: 'var(--ink)', fontWeight: 500 }}>Log in →</Link>
          </div>
        </div>
      </header>

      <main>
        <div className="wrap">
          <a href={MARKETING_HOME} className="back" rel="noopener noreferrer">
            ← Back to home
          </a>

          {/* STAGE 1: FORM */}
          <section className={`stage ${stage === 'form' ? 'on' : ''}`} id="stage-form">
            <div className="stage-head">
              <span className="eyebrow">ResultReview · Free</span>
              <h1>Tell us where you <em>stand.</em></h1>
              <p>Five quick fields. We'll match you against five years of Maharashtra CAP-round data and surface the colleges you can <em>actually</em> get into.</p>
              <div className="progress"><span className="active"></span><span></span><span></span></div>
            </div>

            <form className="form-card" id="prefill-form" onSubmit={(e) => e.preventDefault()}>
              <div className="grid-2col">
                <div className="field">
                  <label className="field-label">Exam</label>
                  <div className="seg">
                    {examOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        className={exam === option ? 'on' : ''}
                        onClick={() => setExam(option)}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="field">
                  <label className="field-label" htmlFor="percentile">Percentile</label>
                  <div className="pct-input-wrap">
                    <input
                      ref={percentileInputRef}
                      id="percentile"
                      type="text"
                      inputMode="decimal"
                      placeholder="97.82"
                      autoComplete="off"
                      aria-invalid={percentileError != null}
                      aria-describedby="percentile-help"
                      value={percentile}
                      onChange={handlePercentileChange}
                      onBlur={handlePercentileBlur}
                      className={percentileError ? "percentile-invalid" : undefined}
                    />
                  </div>
                  <div
                    id="percentile-help"
                    className={`field-help ${percentileError ? "field-help-error" : ""}`}
                  >
                    {percentileError ??
                      `Must be ${PERCENTILE_MIN}–${PERCENTILE_MAX}. Decimal precision matters — 97 and 97.82 are not the same college.`}
                  </div>
                </div>

                <div className="field">
                  <label className="field-label">Category</label>
                  <div className="seg">
                    {verticalReservations.map((reservation) => (
                      <button
                        key={reservation.id}
                        type="button"
                        className={category === reservation.name ? 'on' : ''}
                        title={reservation.description}
                        onClick={() => setCategory(reservation.name)}
                      >
                        {reservation.name}
                      </button>
                    ))}
                  </div>
                  <div className="field-help">Select your vertical reservation category.</div>
                </div>

                <div className="field">
                  <label className="field-label">Gender</label>
                  <div className="seg">
                    {genderOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        className={gender.toLowerCase() === option.toLowerCase() ? 'on' : ''}
                        onClick={() => setGender(option)}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                  <div className="field-help">Used only to surface ladies-quota seats where they apply.</div>
                </div>

                <div className="field">
                  <label className="field-label">Special quota</label>
                  <div className="seg">
                    {horizontalReservations.map((reservation) => (
                      <button
                        key={reservation.id}
                        type="button"
                        className={quotas.includes(reservation.name) ? 'on' : ''}
                        title={reservation.description}
                        onClick={() => toggleQuota(reservation.name)}
                      >
                        {reservation.name}
                      </button>
                    ))}
                  </div>
                  <div className="field-help">Select all horizontal reservations that apply to you.</div>
                </div>
              </div>

              <div className="form-actions">
                <span className="left-note">No account needed for ResultReview</span>
                <button
                  type="button"
                  className="btn"
                  disabled={!percentileValidForSubmit}
                  title={
                    percentileValidForSubmit
                      ? undefined
                      : `Enter a percentile between ${PERCENTILE_MIN} and ${PERCENTILE_MAX}.`
                  }
                  onClick={handleRunResultReview}
                >
                  Run my ResultReview <span className="arr">→</span>
                </button>
              </div>
            </form>
          </section>

          {/* STAGE 2: TERMINAL */}
          <section className={`stage ${stage === 'terminal' ? 'on' : ''}`} id="stage-terminal">
            <div className="stage-head">
              <span className="eyebrow">Step 02 · Analysing</span>
              <h1>Matching you against <em>five years</em> of cutoff data.</h1>
              <p>This usually takes about seven seconds. We're not querying a forum — we're running your inputs against the actual CAP-round closing percentiles.</p>
              {renderChips()}
              <div className="progress"><span className="done"></span><span className="active"></span><span></span></div>
            </div>

            <div className="term-wrap" id="term-steps">
              {terminalSteps.map((s, i) => (
                <div 
                  key={i} 
                  className={`term-card ${visibleSteps.includes(i) ? 'appear' : ''} ${activeStep === i ? 'active' : ''} ${completedSteps.includes(i) ? 'done' : ''}`}
                >
                  <div className="check"></div>
                  <div>
                    <div className="label">{s.label}</div>
                    <div className="sub">{s.sub}</div>
                  </div>
                  <div className="meta">{s.meta}</div>
                </div>
              ))}
            </div>
          </section>

          {/* STAGE 3: RESULTS (BLURRED) */}
          <section className={`stage ${stage === 'results' ? 'on' : ''}`} id="stage-results">
            <div className="stage-head">
              <span className="eyebrow">Step 03 · Your shortlist</span>
              <h1>We found <em><span id="match-count">{previewStats?.collegesMatched ?? "—"}</span> colleges</em> you can realistically get into.</h1>
              <p>Here's the top of your list — ranked by safety, match, and reach. Create a free account to unlock the full list, with cutoffs, branches, and CAP-round breakdowns.</p>
              {renderChips()}
              <div className="progress"><span className="done"></span><span className="done"></span><span className="active"></span></div>
            </div>

            <div className="results">
              <div className="results-grid" aria-hidden="true">
                <div className="results-preview-plate">
                  {Array.from({ length: RESULTS_PREVIEW_SKELETON_ROWS }, (_, i) => (
                    <div key={i} className="results-preview-row">
                      <span className="results-preview-ghost results-preview-ghost--rank" />
                      <div className="results-preview-nameblock">
                        <span className="results-preview-ghost results-preview-ghost--title" />
                        <span className="results-preview-ghost results-preview-ghost--subtitle" />
                      </div>
                      <span className="results-preview-ghost results-preview-ghost--branch" />
                      <span className="results-preview-ghost results-preview-ghost--cutoff" />
                      <span className="results-preview-ghost results-preview-ghost--pill" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="lock">
                <div className="lock-card">
                  <span className="eyebrow">Almost there</span>
                  <h2>Create a <em>free</em> account to see your results.</h2>
                  <p>Your inputs travel with you. We'll prefill the app with your percentile, category, and quota — you'll see your full list on the next screen.</p>

                  <div className="stat-strip">
                    <div className="stat"><div className="n">{previewStats?.collegesMatched ?? "—"}</div><div className="l">Colleges matched</div></div>
                    <div className="stat"><div className="n">{previewStats?.safeBets ?? "—"}</div><div className="l">Safe bets</div></div>
                    <div className="stat"><div className="n">{previewStats?.reachPicks ?? "—"}</div><div className="l">Reach picks</div></div>
                  </div>

                  <Link className="btn" to="/signin" state={{ postAuthRedirect: TRY_NOW_POST_AUTH_REDIRECT }} onClick={handleAuthHandoff}>Create a free account <span className="arr">→</span></Link>
                  <div className="small">Already have one? <Link to="/signin" state={{ postAuthRedirect: TRY_NOW_POST_AUTH_REDIRECT }} onClick={handleAuthHandoff}>Log in</Link></div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
